import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, EMPTY, firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { VisitLogService } from '@core/diagnostics/visit-log.service';
import {
  Basket,
  BasketComparison,
  BranchComparison,
  CityOption,
  CompareLineItem,
  ProductSearchItem
} from '@core/models/models';

const BASKET_STORAGE_KEY = 'commpare-basket-id';
const CITY_STORAGE_KEY = 'commpare-selected-city';

@Component({
  selector: 'app-price-compare',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <header class="top-bar">
        <p class="tagline">השוואת מחירי סופרמרקטים בישראל</p>
        <label class="city-picker">
          <span>עיר</span>
          <select
            data-testid="city-select"
            [(ngModel)]="selectedCity"
            (ngModelChange)="onCityChange($event)"
            [disabled]="citiesLoading || cities.length === 0"
          >
            <option value="">בחר עיר…</option>
            @for (city of cities; track city.value) {
              <option [value]="city.value">{{ city.label }}</option>
            }
          </select>
        </label>
      </header>

      @if (errorMessage) {
        <p class="error" role="alert">{{ errorMessage }}</p>
      }

      <div class="layout">
        <section class="card search-panel">
          <h2>חיפוש מוצרים</h2>
          <input
            class="search-input"
            type="text"
            inputmode="search"
            enterkeyhint="search"
            data-testid="product-search"
            lang="he"
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange($event)"
            placeholder="חפש מוצר… למשל חלב, לחם, ביצים"
            autocomplete="off"
            dir="rtl"
            [disabled]="!selectedCity"
          />

          <div class="results-scroll">
            @if (!selectedCity) {
              <p class="muted hint">בחר עיר כדי להתחיל לחפש.</p>
            } @else if (searchLoading) {
              <p class="muted">מחפש…</p>
            }

            @if (selectedCity && searchResults.length > 0) {
              <ul class="results">
                @for (item of searchResults; track item.id) {
                  <li>
                    <div class="result-main">
                      <strong>{{ item.displayName || item.nameRaw }}</strong>
                      @if (item.brandName) {
                        <span class="brand">{{ item.brandName }}</span>
                      }
                      @if (item.barcode) {
                        <span class="barcode">{{ item.barcode }}</span>
                      }
                      @if (lowestPrice(item); as price) {
                        <span class="price">מ-{{ formatMoney(price.price) }} ₪</span>
                        @if (price.unitPrice != null && price.unitOfMeasure) {
                          <span class="unit-basis">{{ formatMoney(price.unitPrice) }} ₪ ל{{ price.unitOfMeasure }}</span>
                        }
                      }
                    </div>
                    <button type="button" (click)="addToBasket(item)" [disabled]="basketBusy">
                      הוסף
                    </button>
                  </li>
                }
              </ul>
            } @else if (selectedCity && searchQuery.trim().length >= 2 && !searchLoading) {
              <p class="muted">לא נמצאו מוצרים.</p>
            } @else if (selectedCity && searchQuery.trim().length < 2) {
              <p class="muted hint">הקלד לפחות 2 תווים לחיפוש.</p>
            }
          </div>
        </section>

        <aside class="sidebar">
          <section class="card basket-panel" data-testid="basket-panel">
            <div class="section-header">
              <h2>הסל שלי</h2>
              @if (basket) {
                <span class="badge">{{ basket.itemCount }} פריטים</span>
              }
            </div>

            @if (!basket || basket.items.length === 0) {
              <p class="muted">הסל ריק. חפש והוסף מוצרים.</p>
            } @else {
              <ul class="basket-items">
                @for (item of basket.items; track item.productId) {
                  <li>
                    <div class="item-info">
                      <strong>{{ item.productName }}</strong>
                      @if (item.barcode) {
                        <span class="barcode">{{ item.barcode }}</span>
                      }
                    </div>
                    <div class="item-actions">
                      <button type="button" class="qty-btn" (click)="changeQuantity(item.productId, item.quantity - 1)" [disabled]="basketBusy">−</button>
                      <span class="qty">{{ item.quantity }}</span>
                      <button type="button" class="qty-btn" (click)="changeQuantity(item.productId, item.quantity + 1)" [disabled]="basketBusy">+</button>
                      <button type="button" class="remove-btn" (click)="removeItem(item.productId)" [disabled]="basketBusy">הסר</button>
                    </div>
                  </li>
                }
              </ul>

              <button
                type="button"
                class="primary"
                data-testid="compare-prices"
                (click)="compare()"
                [disabled]="!selectedCity || compareLoading || basketBusy"
              >
                {{ compareLoading ? 'משווה…' : 'השווה מחירים' }}
              </button>
            }
          </section>

          @if (comparison) {
            <section class="card compare-panel" data-testid="compare-results">
              <h2>תוצאות השוואה</h2>
              <p class="muted summary">{{ comparison.itemCount }} פריטים · {{ comparison.branches.length }} סניפים</p>

              @if (comparison.branches[0]; as best) {
                <div
                  class="best-summary"
                  [class.complete]="isFullCoverage(best)"
                  [class.partial]="!isFullCoverage(best)"
                >
                  <span class="best-summary-label">{{ highlightLabel(best) }}</span>
                  <strong>{{ best.chainName }} · {{ best.branchName }}</strong>
                  <span class="best-summary-total">{{ formatMoney(best.total) }} ₪</span>
                  @if ((best.savings ?? 0) > 0) {
                    <span class="best-summary-note">
                      מחיר מדף {{ formatMoney(best.shelfTotal ?? 0) }} · חיסכון {{ formatMoney(best.savings ?? 0) }}
                    </span>
                  }
                  @if (!isFullCoverage(best)) {
                    <span class="best-summary-note">
                      {{ best.matchedCount }}/{{ comparison.itemCount }} מוצרים — לא סל שלם
                    </span>
                  }
                </div>
              }

              @if (completeBranches().length > 0) {
                <h3 class="result-group-title">סלים שלמים</h3>
                <ol class="branch-ranking">
                  @for (branch of completeBranches(); track branch.branchId; let i = $index) {
                    <li [class.best]="i === 0">
                      <div class="branch-header">
                        <div>
                          <strong>{{ branch.chainName }} · {{ branch.branchName }}</strong>
                          @if (i === 0) {
                            <span class="best-label">הסל השלם הזול ביותר</span>
                          }
                        </div>
                        <div class="branch-totals">
                          <div class="branch-total">{{ formatMoney(branch.total) }} ₪</div>
                          @if ((branch.savings ?? 0) > 0) {
                            <div class="branch-savings">
                              מדף {{ formatMoney(branch.shelfTotal ?? 0) }} · חיסכון {{ formatMoney(branch.savings ?? 0) }}
                            </div>
                          }
                        </div>
                      </div>
                      <div class="branch-meta">
                        <span>כיסוי: {{ formatPercent(branch.coverage) }}</span>
                        <span>{{ branch.matchedCount }}/{{ comparison.itemCount }}</span>
                      </div>
                      @if (branch.items.length > 0) {
                        <details class="priced-details">
                          <summary>פירוט מחירים ({{ branch.matchedCount }})</summary>
                          <ul class="priced-items">
                            @for (line of branch.items; track line.productId) {
                              <li>
                                <span class="line-copy">
                                  <span>{{ line.productName }} × {{ line.quantity }}</span>
                                  @if (line.promoLabel) {
                                    <span class="promo-flag">מבצע</span>
                                    <span class="promo-text">{{ line.promoLabel }}</span>
                                  }
                                </span>
                                <span class="line-price">
                                  @if (hasPromoMath(line)) {
                                    <span class="shelf-struck">{{ formatMoney(shelfLineTotal(line)) }} ₪</span>
                                  }
                                  {{ formatMoney(line.lineTotal ?? 0) }} ₪
                                  @if (line.unitPrice != null) {
                                    <span class="unit-price">({{ formatMoney(line.unitPrice) }} ₪/יח')</span>
                                  }
                                </span>
                              </li>
                            }
                          </ul>
                        </details>
                      }
                    </li>
                  }
                </ol>
              }

              @if (partialBranches().length > 0) {
                <h3 class="result-group-title partial-group">כיסוי חלקי</h3>
                <ol class="branch-ranking">
                  @for (branch of partialBranches(); track branch.branchId; let i = $index) {
                    <li
                      class="incomplete"
                      [class.best-partial]="completeBranches().length === 0 && i === 0"
                    >
                      <div class="branch-header">
                        <div>
                          <strong>{{ branch.chainName }} · {{ branch.branchName }}</strong>
                          @if (completeBranches().length === 0 && i === 0) {
                            <span class="best-label partial-label">הסל הזמין הטוב ביותר</span>
                          }
                        </div>
                        <div class="branch-totals">
                          <div class="branch-total">{{ formatMoney(branch.total) }} ₪</div>
                          @if ((branch.savings ?? 0) > 0) {
                            <div class="branch-savings">
                              מדף {{ formatMoney(branch.shelfTotal ?? 0) }} · חיסכון {{ formatMoney(branch.savings ?? 0) }}
                            </div>
                          }
                        </div>
                      </div>
                      <div class="branch-meta">
                        <span>כיסוי: {{ formatPercent(branch.coverage) }}</span>
                        <span>{{ branch.matchedCount }}/{{ comparison.itemCount }}</span>
                        <span class="missing-count">{{ branch.missingCount }} חסרים</span>
                      </div>
                      @if (branch.missing.length > 0) {
                        <details class="missing-details">
                          <summary>פריטים חסרים</summary>
                          <ul>
                            @for (missing of branch.missing; track missing.productId) {
                              <li>{{ missing.productName }} × {{ missing.quantity }}</li>
                            }
                          </ul>
                        </details>
                      }
                      @if (branch.items.length > 0) {
                        <details class="priced-details">
                          <summary>פירוט מחירים ({{ branch.matchedCount }})</summary>
                          <ul class="priced-items">
                            @for (line of branch.items; track line.productId) {
                              <li>
                                <span class="line-copy">
                                  <span>{{ line.productName }} × {{ line.quantity }}</span>
                                  @if (line.promoLabel) {
                                    <span class="promo-flag">מבצע</span>
                                    <span class="promo-text">{{ line.promoLabel }}</span>
                                  }
                                </span>
                                <span class="line-price">
                                  @if (hasPromoMath(line)) {
                                    <span class="shelf-struck">{{ formatMoney(shelfLineTotal(line)) }} ₪</span>
                                  }
                                  {{ formatMoney(line.lineTotal ?? 0) }} ₪
                                  @if (line.unitPrice != null) {
                                    <span class="unit-price">({{ formatMoney(line.unitPrice) }} ₪/יח')</span>
                                  }
                                </span>
                              </li>
                            }
                          </ul>
                        </details>
                      }
                    </li>
                  }
                </ol>
              }
            </section>
          }
        </aside>
      </div>
    </div>
  `,
  styles: [
    `
      .page {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem 1rem 2rem;
        box-sizing: border-box;
      }

      .top-bar {
        margin-bottom: 1rem;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .city-picker {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.85rem;
        color: #444;
        min-width: 11rem;
      }

      .city-picker select {
        font: inherit;
        font-size: 0.95rem;
        padding: 0.45rem 0.6rem;
        border: 1px solid #ccc;
        border-radius: 8px;
        background: #fff;
        direction: rtl;
      }

      h2 {
        margin: 0 0 0.75rem;
        font-size: 1.05rem;
      }

      .tagline {
        margin: 0;
        color: #444;
        font-size: 0.95rem;
        align-self: center;
      }

      .layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 340px;
        gap: 1rem;
        align-items: start;
      }

      .sidebar {
        position: sticky;
        top: 1rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        max-height: calc(100vh - 2rem);
        overflow-y: auto;
      }

      .card {
        background: #fff;
        border: 1px solid #e5e5e5;
        border-radius: 12px;
        padding: 1rem;
      }

      .search-panel {
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 6rem);
        max-height: calc(100vh - 6rem);
      }

      .results-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        margin-top: 0.75rem;
        padding-inline-end: 0.25rem;
      }

      .search-input {
        width: 100%;
        box-sizing: border-box;
        padding: 0.65rem 0.85rem;
        font-size: 1rem;
        border: 1px solid #ccc;
        border-radius: 8px;
        flex-shrink: 0;
        direction: rtl;
        text-align: right;
        unicode-bidi: plaintext;
      }

      .search-input::placeholder {
        direction: rtl;
        text-align: right;
        unicode-bidi: plaintext;
      }

      .search-input::-webkit-input-placeholder {
        direction: rtl;
        text-align: right;
      }

      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
      }

      .section-header h2 {
        margin: 0;
      }

      .badge {
        background: #eef4ff;
        color: #1a4fd6;
        padding: 0.2rem 0.6rem;
        border-radius: 999px;
        font-size: 0.8rem;
        white-space: nowrap;
      }

      .muted {
        color: #666;
        margin: 0;
      }

      .summary {
        margin-bottom: 0.75rem;
        font-size: 0.9rem;
      }

      .hint {
        padding-top: 0.5rem;
      }

      .error {
        color: #b00020;
        background: #fdecee;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        margin-bottom: 1rem;
      }

      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .results li,
      .basket-items li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.6rem 0;
        border-bottom: 1px solid #f0f0f0;
      }

      .results li:last-child,
      .basket-items li:last-child {
        border-bottom: none;
      }

      .result-main,
      .item-info {
        display: flex;
        flex-direction: column;
        gap: 0.1rem;
        min-width: 0;
      }

      .result-main strong,
      .item-info strong {
        font-size: 0.92rem;
        line-height: 1.3;
      }

      .brand,
      .barcode,
      .unit-basis {
        font-size: 0.8rem;
        color: #666;
      }

      .price {
        color: #0a7a3e;
        font-size: 0.85rem;
      }

      button {
        cursor: pointer;
        border: 1px solid #ccc;
        background: #fff;
        border-radius: 8px;
        padding: 0.4rem 0.65rem;
        font: inherit;
        font-size: 0.9rem;
        flex-shrink: 0;
      }

      button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      button.primary {
        margin-top: 0.75rem;
        width: 100%;
        background: #1a4fd6;
        color: #fff;
        border-color: #1a4fd6;
        padding: 0.7rem;
        font-weight: 600;
        font-size: 1rem;
      }

      .item-actions {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        flex-shrink: 0;
      }

      .qty-btn {
        width: 1.75rem;
        padding-inline: 0;
      }

      .qty {
        min-width: 1.25rem;
        text-align: center;
        font-size: 0.9rem;
      }

      .remove-btn {
        color: #b00020;
        border-color: #f0c4c8;
        font-size: 0.8rem;
        padding: 0.35rem 0.5rem;
      }

      .best-summary {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.35rem 0.5rem;
        border-radius: 8px;
        padding: 0.65rem 0.75rem;
        margin-bottom: 0.75rem;
        font-size: 0.9rem;
      }

      .best-summary.complete {
        background: #f3fbf6;
        border: 1px solid #0a7a3e;
      }

      .best-summary.partial {
        background: #fff8ee;
        border: 1px solid #c4892a;
      }

      .best-summary-label {
        font-weight: 600;
        font-size: 0.8rem;
      }

      .best-summary.complete .best-summary-label {
        color: #0a7a3e;
      }

      .best-summary.partial .best-summary-label {
        color: #8a5a00;
      }

      .best-summary-total {
        margin-inline-start: auto;
        font-weight: 700;
        font-size: 1.1rem;
      }

      .best-summary-note {
        flex-basis: 100%;
        font-size: 0.8rem;
        color: #8a5a00;
      }

      .result-group-title {
        margin: 0.85rem 0 0.45rem;
        font-size: 0.85rem;
        font-weight: 600;
        color: #0a7a3e;
      }

      .result-group-title.partial-group {
        color: #8a5a00;
        margin-top: 1rem;
        padding-top: 0.75rem;
        border-top: 1px dashed #e0c48a;
      }

      .branch-ranking {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .branch-ranking > li {
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        padding: 0.75rem;
        font-size: 0.9rem;
      }

      .branch-ranking > li.best {
        border-color: #0a7a3e;
        background: #f3fbf6;
      }

      .branch-ranking > li.best-partial {
        border-color: #c4892a;
        background: #fff8ee;
      }

      .branch-ranking > li.incomplete {
        border-style: dashed;
      }

      .branch-header {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        align-items: flex-start;
      }

      .branch-totals {
        text-align: start;
      }

      .branch-total {
        font-size: 1.05rem;
        font-weight: 700;
        white-space: nowrap;
      }

      .branch-savings {
        font-size: 0.75rem;
        font-weight: 500;
        color: #0a7a3e;
        white-space: nowrap;
      }

      .best-label {
        display: inline-block;
        margin-inline-start: 0.35rem;
        font-size: 0.75rem;
        color: #0a7a3e;
        font-weight: 600;
      }

      .best-label.partial-label {
        color: #8a5a00;
      }

      .branch-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-top: 0.35rem;
        font-size: 0.82rem;
        color: #555;
      }

      .missing-count {
        color: #b00020;
      }

      .missing-details {
        margin-top: 0.5rem;
        font-size: 0.82rem;
      }

      .missing-details ul {
        margin-top: 0.35rem;
        padding-inline-start: 1.1rem;
        list-style: disc;
      }

      .priced-details {
        margin-top: 0.5rem;
        font-size: 0.82rem;
      }

      .priced-items li {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.25rem 0;
        border-bottom: none;
      }

      .line-copy {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.25rem 0.4rem;
        min-width: 0;
      }

      .promo-flag {
        color: #b42318;
        font-size: 0.75rem;
        font-weight: 700;
      }

      .promo-text {
        color: #8a5a00;
        font-size: 0.75rem;
      }

      .line-price {
        white-space: nowrap;
        font-weight: 600;
      }

      .shelf-struck {
        font-weight: 400;
        color: #888;
        text-decoration: line-through;
        margin-inline-end: 0.25rem;
      }

      .unit-price {
        font-weight: 400;
        color: #666;
        font-size: 0.78rem;
      }

      @media (max-width: 860px) {
        .layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          position: static;
          max-height: none;
          order: -1;
        }

        .search-panel {
          min-height: 50vh;
          max-height: 60vh;
        }
      }
    `
  ]
})
export class PriceCompareComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly visitLog = inject(VisitLogService);
  private readonly searchSubject = new Subject<string>();
  private searchSub = this.searchSubject.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap((query) => {
      const trimmed = query.trim();
      if (!this.selectedCity || trimmed.length < 2) {
        this.searchResults = [];
        this.searchLoading = false;
        return EMPTY;
      }
      this.visitLog.noteSearch(trimmed);
      this.searchLoading = true;
      this.errorMessage = null;
      return this.api.searchProducts(trimmed, this.selectedCity).pipe(
        catchError((err) => {
          this.errorMessage = this.readError(err);
          this.searchResults = [];
          this.searchLoading = false;
          return of(null);
        })
      );
    })
  ).subscribe((response) => {
    this.searchLoading = false;
    if (response) {
      this.searchResults = response.items;
    }
  });

  searchQuery = '';
  searchResults: ProductSearchItem[] = [];
  searchLoading = false;
  cities: CityOption[] = [];
  citiesLoading = false;
  selectedCity = '';
  basket: Basket | null = null;
  comparison: BasketComparison | null = null;
  basketBusy = false;
  compareLoading = false;
  errorMessage: string | null = null;

  ngOnInit(): void {
    this.citiesLoading = true;
    this.api.listCities().subscribe({
      next: (response) => {
        this.cities = response.cities;
        this.citiesLoading = false;
        const storedCity = localStorage.getItem(CITY_STORAGE_KEY) ?? '';
        if (storedCity && this.cities.some((city) => city.value === storedCity)) {
          this.selectedCity = storedCity;
        }
      },
      error: (err) => {
        this.citiesLoading = false;
        this.errorMessage = this.readError(err);
      }
    });

    const storedId = localStorage.getItem(BASKET_STORAGE_KEY);
    if (storedId) {
      const id = Number(storedId);
      if (Number.isFinite(id)) {
        this.api.getBasket(id).subscribe({
          next: (basket) => {
            this.basket = basket;
          },
          error: () => {
            localStorage.removeItem(BASKET_STORAGE_KEY);
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.searchSub.unsubscribe();
  }

  onCityChange(value: string): void {
    this.selectedCity = value;
    this.searchResults = [];
    this.comparison = null;
    if (value) {
      localStorage.setItem(CITY_STORAGE_KEY, value);
      if (this.searchQuery.trim().length >= 2) {
        this.searchSubject.next(this.searchQuery);
      }
    } else {
      localStorage.removeItem(CITY_STORAGE_KEY);
    }
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  async addToBasket(item: ProductSearchItem): Promise<void> {
    this.basketBusy = true;
    this.errorMessage = null;
    this.comparison = null;

    try {
      if (!this.basket) {
        this.basket = await firstValueFrom(this.api.createBasket());
        localStorage.setItem(BASKET_STORAGE_KEY, String(this.basket.id));
      }

      this.basket = await firstValueFrom(
        this.api.upsertBasketItem(this.basket.id, item.id, 1)
      );
    } catch (err) {
      this.errorMessage = this.readError(err);
    } finally {
      this.basketBusy = false;
    }
  }

  async changeQuantity(productId: number, quantity: number): Promise<void> {
    if (!this.basket || this.basketBusy) return;

    this.basketBusy = true;
    this.errorMessage = null;
    this.comparison = null;

    try {
      if (quantity <= 0) {
        this.basket = await firstValueFrom(this.api.removeBasketItem(this.basket.id, productId));
      } else {
        this.basket = await firstValueFrom(
          this.api.upsertBasketItem(this.basket.id, productId, quantity)
        );
      }
    } catch (err) {
      this.errorMessage = this.readError(err);
    } finally {
      this.basketBusy = false;
    }
  }

  removeItem(productId: number): void {
    void this.changeQuantity(productId, 0);
  }

  compare(): void {
    if (!this.basket || !this.selectedCity) return;

    this.compareLoading = true;
    this.errorMessage = null;

    this.api.compareBasket(this.basket.id, this.selectedCity).subscribe({
      next: (result) => {
        this.comparison = result;
        this.compareLoading = false;
      },
      error: (err) => {
        this.errorMessage = this.readError(err);
        this.compareLoading = false;
      }
    });
  }

  lowestPrice(item: ProductSearchItem) {
    if (!item.prices.length) return null;
    return item.prices.reduce((min, p) => (p.price < min.price ? p : min));
  }

  formatMoney(value: number): string {
    return value.toFixed(2);
  }

  hasPromoMath(line: CompareLineItem): boolean {
    return line.promoPrice != null;
  }

  shelfLineTotal(line: CompareLineItem): number {
    const unit = line.shelfPrice ?? line.price ?? 0;
    return unit * line.quantity;
  }

  formatPercent(value: number): string {
    return `${Math.round(value * 100)}%`;
  }

  isFullCoverage(branch: BranchComparison): boolean {
    return branch.missingCount === 0;
  }

  highlightLabel(branch: BranchComparison): string {
    return this.isFullCoverage(branch)
      ? 'הסל השלם הזול ביותר'
      : 'הסל הזמין הטוב ביותר';
  }

  completeBranches(): BranchComparison[] {
    return this.comparison?.branches.filter((branch) => this.isFullCoverage(branch)) ?? [];
  }

  partialBranches(): BranchComparison[] {
    return this.comparison?.branches.filter((branch) => !this.isFullCoverage(branch)) ?? [];
  }

  private readError(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: unknown }).error;
      if (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string') {
        return (body as { error: string }).error;
      }
    }
    return 'משהו השתבש. נסה שוב.';
  }
}
